import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { CommissionsOverview } from '../mockCommissionsData';
import { formatCurrencyAmount, formatPercentageValue } from '../../../../../utils/miscFunctions';

export interface ICommissionsMonthlyTrend {
  months: string[],
  earnedAmounts: number[],
  paidAmounts: number[],
  currency: string
}

interface CommissionTrendsChartProps {
  data: ICommissionsMonthlyTrend;
}

const CommissionTrendsChart: React.FC<CommissionTrendsChartProps> = ({ data }) => {
  // Check if dark mode is active
  const isDarkMode = document.documentElement.classList.contains('dark');

  // Calculate average earned commission
  const avgEarned = data.earnedAmounts.reduce((sum: number, item: any) => sum + item.earned, 0) / 
    data.earnedAmounts.length;

  // Calculate average paid commission
  const avgPaid = data.paidAmounts.reduce((sum: number, item: any) => sum + item.paid, 0) / 
    data.paidAmounts.length;
  
  // Calculate commission collection rate
  const collectionRate = (avgPaid / avgEarned) * 100;

  return (
    <div className="bg-white dark:bg-darkBg rounded-2xl shadow-sm border border-gray-200 dark:border-darkBg-border p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">Monthly Commission Trends</h2>
          <p className="text-sm text-gray-600 dark:text-darkBg-subText">Earned vs. Paid Commission</p>
        </div>
        {collectionRate > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg px-3 py-1">
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              {formatPercentageValue(Number(collectionRate.toFixed(1)))}% collection rate
            </span>
          </div>
        )}
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data.months.map((month, index) => ({
              month,
              earned: data.earnedAmounts[index] || 0,
              paid: data.paidAmounts[index] || 0
            }))}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} stroke="#e5e7eb" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12, fill: isDarkMode ? '#9ca3af' : '#374151' }}
              padding={{ left: 10, right: 10 }}
            />
            <YAxis 
              tickFormatter={(value: number) => formatCurrencyAmount(value)}
              tick={{ fontSize: 12, fill: isDarkMode ? '#9ca3af' : '#374151' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip 
              formatter={(value: number) => [formatCurrencyAmount(value), '']}
              labelFormatter={(label) => `Month: ${label}`}
              contentStyle={{ 
                backgroundColor: isDarkMode ? '#1f2937' : 'rgba(255, 255, 255, 0.95)',
                borderRadius: '4px',
                padding: '10px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                border: isDarkMode ? '1px solid #374151' : 'none'
              }}
            />
            <Legend 
              wrapperStyle={{ paddingTop: 10 }}
              formatter={(value) => <span style={{ color: isDarkMode ? '#9ca3af' : '#374151', fontSize: '12px' }}>{value}</span>}
            />
            <ReferenceLine 
              y={avgEarned} 
              stroke="#4f46e5" 
              strokeDasharray="3 3"
              label={{ 
                value: `Avg Earned: ${formatCurrencyAmount(avgEarned)}`, 
                position: 'insideBottomRight',
                fill: '#4f46e5',
                fontSize: 11
              }} 
            />
            <ReferenceLine 
              y={avgPaid} 
              stroke="#10b981" 
              strokeDasharray="3 3"
              label={{ 
                value: `Avg Paid: ${formatCurrencyAmount(avgPaid)}`, 
                position: 'insideTopRight',
                fill: '#10b981',
                fontSize: 11
              }} 
            />
            <Bar 
              dataKey="earned" 
              name="Earned Commission" 
              fill="#4f46e5" 
              radius={[4, 4, 0, 0]} 
              barSize={30}
              fillOpacity={0.8}
            />
            <Bar 
              dataKey="paid" 
              name="Paid Commission" 
              fill="#10b981" 
              radius={[4, 4, 0, 0]} 
              barSize={30}
              fillOpacity={0.8}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 grid grid-cols-2 gap-4 text-center">
        <div className="p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
          <p className="text-sm font-medium text-gray-600 dark:text-darkBg-subText">Total Earned (YTD)</p>
          <p className="text-lg font-bold text-indigo-700 dark:text-indigo-300">
            {formatCurrencyAmount(data.earnedAmounts.reduce((sum, item) => sum + item, 0))}
          </p>
        </div>
        <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
          <p className="text-sm font-medium text-gray-600 dark:text-darkBg-subText">Total Received (YTD)</p>
          <p className="text-lg font-bold text-green-700 dark:text-green-300">
            {formatCurrencyAmount(data.paidAmounts.reduce((sum, item) => sum + item, 0))}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CommissionTrendsChart;
